import { useCallback, useEffect, useRef } from 'react';
import { getArrayLength, getHasElementsInArray } from 'utils';

/**
 * `overlay` for layers that render above the page (modals), `base` for layers
 * that live in it (drawers, panels, inline banners). An overlay always sits
 * above every base layer, no matter which of them opened first.
 */
export type EscapeLayerLevel = 'base' | 'overlay';

// The object identity is the layer's id, so no counter is needed.
type EscapeLayer = { level: EscapeLayerLevel };

/**
 * `ncoded-component-library` layers (`Modal`, `Dropdown`, and everything built
 * on them - `Select`, table panels) close themselves on Escape through their
 * own stack, on `keyup`, instead of registering here, so they are looked up in
 * the DOM. Their overlay only exists while they are open.
 *
 * Hover popups (`ResolvePopup`) render an overlay too, but Escape does not
 * close them, so they must not swallow the press.
 */
const LIBRARY_OVERLAY_SELECTOR = '.ncoded-modal, .dropdown__overlay';

// Ordered by open time - the last one is the most recently opened.
const layers: EscapeLayer[] = [];

const hasOpenLibraryOverlay = () =>
  !!document.querySelector(LIBRARY_OVERLAY_SELECTOR);

/**
 * Overlays are portalled to the end of the body as they open, and a nested one
 * (a modal opened from a modal, a dropdown opened inside it) therefore comes
 * after the layer it covers - the same document order that stacks them
 * visually.
 */
const hasLibraryOverlayAbove = (element: Element) =>
  Array.from(document.querySelectorAll(LIBRARY_OVERLAY_SELECTOR)).some(
    (overlay) =>
      !!(
        element.compareDocumentPosition(overlay) &
        Node.DOCUMENT_POSITION_FOLLOWING
      ),
  );

const getTopLayer = () => {
  const overlays = layers.filter(({ level }) => level === 'overlay');
  const topLevelLayers = getHasElementsInArray(overlays) ? overlays : layers;

  return topLevelLayers[getArrayLength(topLevelLayers) - 1];
};

type UseEscapeLayerOptions = {
  enabled?: boolean;
  level?: EscapeLayerLevel;
  /**
   * The layer's own overlay node - `ModalRef.container` for a modal. An
   * `overlay` layer needs it to tell the overlays stacked above it from the
   * ones below, itself included; a `base` layer never sits among them, so it
   * can leave this out.
   */
  element?: Element;
};

/**
 * Registers a layer that Escape can close, so one press closes exactly one
 * layer - the top one. Returns a getter to be called from the key handler
 * itself, never during render, since the stack changes as layers open.
 *
 * Used through `useCloseOnEscape`; register directly only for a layer that
 * closes on Escape some other way.
 */
export default function useEscapeLayer(options: UseEscapeLayerOptions = {}) {
  const { enabled = true, level = 'base', element } = options;

  const layerRef = useRef<EscapeLayer>();

  useEffect(() => {
    if (!enabled) return;

    const layer: EscapeLayer = { level };

    layerRef.current = layer;
    layers.push(layer);

    return () => {
      const index = layers.indexOf(layer);

      if (index !== -1) layers.splice(index, 1);
      if (layerRef.current === layer) layerRef.current = undefined;
    };
  }, [enabled, level]);

  return useCallback(() => {
    if (!layerRef.current || getTopLayer() !== layerRef.current) return false;

    // A library modal or dropdown above this layer takes the press for itself,
    // which is what leaves this layer to the next press.
    if (element) return !hasLibraryOverlayAbove(element);

    // Every one of them is above a base layer, so an open one is enough. An
    // overlay layer without an `element` cannot tell them apart and keeps the
    // press.
    return level === 'overlay' || !hasOpenLibraryOverlay();
  }, [element, level]);
}
